'use client'

import { useReferencesProductFilters } from '@/feature/product/hooks/useReferencesProductFilters'
import { createApplyDiscriminantFiltersDrawerIntent } from '@/feature/product/services/product.intents'
import {
  referencesProductArticlesAtom,
  referencesProductArticlesFilteredAtom,
  referencesProductFiltersEnrichedAtom,
} from '@/feature/product/services/referencesProduct.store'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Accordion } from '@/ui/accordion/Accordion'
import { AccordionItem } from '@/ui/accordion/AccordionItem'
import { Drawer, DrawerAction, DrawerBody, DrawerFooter } from '@/ui/drawer/drawer/Drawer.client'
import { FilterGroup } from '@/ui/form/inputs/filter-group/FilterGroup.client'
import { useAtom, useAtomValue } from 'jotai'
import { useCallback, useEffect, useRef, useState } from 'react'

export type ReferencesProductFiltersMobileDrawerProps = {
  isOpen: boolean
  onCloseAction: () => void
}

export function ReferencesProductFiltersMobileDrawer({
  isOpen,
  onCloseAction,
}: ReferencesProductFiltersMobileDrawerProps) {
  const { t } = useTranslation(['referencesProductFilters'])
  const [enrichedFilters, setEnrichedFilters] = useAtom(referencesProductFiltersEnrichedAtom)
  const referencesProductArticles = useAtomValue(referencesProductArticlesAtom)
  const referencesProductArticlesFiltered = useAtomValue(referencesProductArticlesFilteredAtom)
  const [mobileTempFilters, setMobileTempFilters] = useState([...enrichedFilters])
  const enrichedFiltersRef = useRef(enrichedFilters)
  enrichedFiltersRef.current = enrichedFilters

  const { totalActivesFilters, handleResetFilters, handleFilterChange, handleUpdateFilters } =
    useReferencesProductFilters()

  const getCurrentFiltersFromUrl = useCallback(() => {
    if (typeof window === 'undefined') return {}
    const urlParams = new URLSearchParams(window.location.search)

    return Object.fromEntries(urlParams)
  }, [])

  const handleApplyFilters = () => {
    handleUpdateFilters(enrichedFilters)
    pushToGtmDataLayer({
      event: 'gaEvent',
      eventCategory: 'fiche produit multi-discriminant',
      eventAction: 'tableau de références - mobile',
      eventLabel: 'appliquer les filtres',
    })
    onCloseAction()
  }

  const titleSentence =
    referencesProductArticlesFiltered.length === referencesProductArticles.length
      ? 'referencesProductFilters:title'
      : 'referencesProductFilters:titleSelected'

  useEffect(() => {
    if (isOpen) {
      setMobileTempFilters([...enrichedFiltersRef.current])
    }
  }, [isOpen])

  return (
    <Drawer
      isOpen={isOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setEnrichedFilters([...mobileTempFilters])
          handleUpdateFilters(mobileTempFilters)
          onCloseAction()
        }
      }}
      size="lg"
      title={t(titleSentence, {
        count: referencesProductArticlesFiltered.length,
        total: referencesProductArticles.length,
      })}
    >
      <DrawerBody>
        <Accordion>
          {enrichedFilters.map((filter, index) => {
            // TODO: Create scalar for filter label `FilterLabel: string & { __type: "FilterLabel" }`
            return (
              <AccordionItem id={filter.label} key={filter.label}>
                <AccordionItem.Header
                  title={filter.label}
                  notificationCount={
                    filter.valuesChecked.length > 0 ? filter.valuesChecked.length : undefined
                  }
                />
                <AccordionItem.Panel>
                  <FilterGroup
                    multiple
                    hideTitle
                    filter={{
                      label: String(index),
                      values: filter.values!.map(({ label }) => ({ label, value: label })),
                    }}
                    selectedValues={filter.valuesChecked}
                    disabledValues={filter.values
                      .filter((value) => value.disabled)
                      .map((value) => value.label)}
                    onSelectionChange={(label, value) => handleFilterChange(label, value)}
                  />
                </AccordionItem.Panel>
              </AccordionItem>
            )
          })}
        </Accordion>
      </DrawerBody>

      <DrawerFooter>
        <DrawerAction
          type="custom"
          handle="reset-filters"
          variant="secondary"
          action={async (): Promise<false> => {
            handleResetFilters()

            return false
          }}
          isDisabled={totalActivesFilters === 0}
          intent={createApplyDiscriminantFiltersDrawerIntent({})}
        >
          {t('referencesProductFilters:resetDrawer')}
        </DrawerAction>
        <DrawerAction
          type="custom"
          handle="apply-filters"
          variant="primary"
          action={handleApplyFilters}
          intent={createApplyDiscriminantFiltersDrawerIntent(getCurrentFiltersFromUrl())}
        >
          {t('referencesProductFilters:validate')}
        </DrawerAction>
      </DrawerFooter>
    </Drawer>
  )
}
